import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Toolbar, Box, List, ListItemButton, ListItemIcon, ListItemText, Divider, Collapse, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import InsightsIcon from '@mui/icons-material/Insights';
import SearchIcon from '@mui/icons-material/Search';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InboxIcon from '@mui/icons-material/Inbox';
import WidgetsIcon from '@mui/icons-material/Widgets';
// import SettingsIcon from '@mui/icons-material/Settings'; // Removed as Session Settings is moved
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import EditNoteIcon from '@mui/icons-material/EditNote';
import UpgradeIcon from '@mui/icons-material/Upgrade'; // Assuming this exists or use generic
import LogoutIcon from '@mui/icons-material/Logout';
import ChatwootIntegrationsList from './ChatwootIntegrationsList';

export default function LeftNavDrawer({
  open,
  onClose,
  width = 280,
  onCreateSession,
  onOpenFiles,
  sessions = [],
  onSelectSession,
  onRenameSession,
  onDeleteSession,
  onOpenInvite,
  onOpenChatwootSettings,
  onOpenLocalChatwootSettings,
  onOpenUpload,
  onOpenUsageBilling,
  onOpenCalCom,
  onOpenFAQManagement,
  onOpenEcommerceIntegrations,

  onOpenSessionSettings, // Kept in prop destructuring just in case, but unused in render
  onOpenDefaultPrompt,
  onLogout,
  hideChat = false, // NEW: If true, hide Chat (New), Customer Inbox, and History sections
}) {
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [dashboardOpen, setDashboardOpen] = React.useState(false);
  const [integrationsOpen, setIntegrationsOpen] = React.useState(false);
  const [chatwootIntegrationsOpen, setChatwootIntegrationsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filteredSessions = React.useMemo(() => {
    const s = (search || '').toLowerCase();
    return (sessions || []).filter(ss => (ss.name || 'Chat').toLowerCase().includes(s));
  }, [sessions, search]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width, boxSizing: 'border-box' } }}
    >
      <Toolbar />
      <List dense>
        {/* Chat section - hidden on /inbox */}
        {!hideChat && (
          <>
            <ListItemButton onClick={() => { if (onCreateSession) onCreateSession(); if (onClose) onClose(); }}>
              <ListItemIcon><ChatOutlinedIcon /></ListItemIcon>
              <ListItemText primary="Chat (New)" />
            </ListItemButton>

            <ListItemButton onClick={() => { navigate('/inbox'); if (onClose) onClose(); }}>
              <ListItemIcon><InboxIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Customer Inbox" primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>

            {false && (
              <ListItemButton onClick={() => { if (onOpenUsageBilling) onOpenUsageBilling(); if (onClose) onClose(); }}>
                <ListItemIcon><InsightsIcon /></ListItemIcon>
                <ListItemText primary="Usage & Billing" />
              </ListItemButton>
            )}
            <ListItemButton onClick={() => setHistoryOpen(v => !v)}>
              <ListItemIcon><HistoryIcon /></ListItemIcon>
              <ListItemText primary="History" />
              {historyOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={historyOpen} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, pb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search chats…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                    )
                  }}
                />
              </Box>
              <List component="div" disablePadding dense sx={{ maxHeight: '40vh', overflow: 'auto' }}>
                {filteredSessions.map((ss) => (
                  <Box key={ss.id} sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemButton sx={{ pl: 7, pr: 1, flexGrow: 1 }} onClick={() => { if (onSelectSession) onSelectSession(ss.id); if (onClose) onClose(); }}>
                      <ListItemIcon><ChatOutlinedIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={ss.name || 'Chat'} primaryTypographyProps={{ noWrap: true }} />
                    </ListItemButton>
                    <IconButton size="small" title="Rename" onClick={(e) => { e.stopPropagation(); if (onRenameSession) onRenameSession(ss.id); }}>
                      <DriveFileRenameOutlineIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Delete" onClick={(e) => { e.stopPropagation(); if (onDeleteSession) onDeleteSession(ss.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
      <Divider />
      <Box sx={{ px: 1.5, pt: 1 }}>
        <Typography variant="caption" color="text.secondary">Dashboard</Typography>
      </Box>
      <List dense>
        <ListItemButton onClick={() => setDashboardOpen(v => !v)}>
          <ListItemIcon><FolderIcon /></ListItemIcon>
          <ListItemText primary="Dashboard" />
          {dashboardOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={dashboardOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>
            <ListItemButton sx={{ pl: 7 }} onClick={() => { if (onOpenFiles) onOpenFiles(); if (onClose) onClose(); }}>
              <ListItemIcon><FolderIcon /></ListItemIcon>
              <ListItemText primary="Knowledge Base" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 7 }} onClick={() => { if (onOpenFAQManagement) onOpenFAQManagement(); if (onClose) onClose(); }}>
              <ListItemIcon><QuestionAnswerIcon /></ListItemIcon>
              <ListItemText primary="FAQ Management" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 7 }} onClick={() => { navigate('/widget-settings'); if (onClose) onClose(); }}>
              <ListItemIcon><WidgetsIcon /></ListItemIcon>
              <ListItemText primary="Widget Settings" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 7 }} onClick={() => { navigate('/channels'); if (onClose) onClose(); }}>
              <ListItemIcon><ChatOutlinedIcon /></ListItemIcon>
              <ListItemText primary="Channels" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 7 }} onClick={() => { if (onOpenInvite) onOpenInvite(); if (onClose) onClose(); }}>
              <ListItemIcon><GroupAddIcon /></ListItemIcon>
              <ListItemText primary="Invite Team Member" />
            </ListItemButton>
            <ListItemButton sx={{ pl: 7 }} onClick={() => { if (onOpenUpload) onOpenUpload(); if (onClose) onClose(); }}>
              <ListItemIcon><FolderIcon /></ListItemIcon>
              <ListItemText primary="Upload & Delete Files" />
            </ListItemButton>

            {/* Integrations Section Nested under Dashboard */}
            <ListItemButton sx={{ pl: 7 }} onClick={() => setIntegrationsOpen(v => !v)}>
              <ListItemIcon><FolderIcon /></ListItemIcon>
              <ListItemText primary="Integrations" />
              {integrationsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={integrationsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {/* Chatwoot Integrations */}
                <ListItemButton sx={{ pl: 9 }} onClick={() => setChatwootIntegrationsOpen(v => !v)}>
                  <ListItemIcon><ChatOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Chatwoot" />
                  {chatwootIntegrationsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={chatwootIntegrationsOpen} timeout="auto" unmountOnExit>
                  <ChatwootIntegrationsList
                    onSelectIntegration={(integrationId) => {
                      if (onOpenLocalChatwootSettings) onOpenLocalChatwootSettings(integrationId);
                    }}
                    onCreateNew={() => {
                      if (onOpenLocalChatwootSettings) onOpenLocalChatwootSettings(null);
                    }}
                    onClose={onClose}
                  />
                </Collapse>

                {/* Cal.com Integration */}
                <ListItemButton sx={{ pl: 9 }} onClick={() => { if (onOpenCalCom) onOpenCalCom(); if (onClose) onClose(); }}>
                  <ListItemIcon><CalendarMonthIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Cal.com" />
                </ListItemButton>

                {/* E-commerce Integrations */}
                <ListItemButton sx={{ pl: 9 }} onClick={() => { if (onOpenEcommerceIntegrations) onOpenEcommerceIntegrations(); if (onClose) onClose(); }}>
                  <ListItemIcon><StorefrontIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="E-commerce" />
                </ListItemButton>
              </List>
            </Collapse>



            <ListItemButton sx={{ pl: 7 }} onClick={() => { if (onOpenUsageBilling) onOpenUsageBilling(); if (onClose) onClose(); }}>
              <ListItemIcon><InsightsIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Usage & Billing" />
            </ListItemButton>
          </List>
        </Collapse>

      </List>

      <Divider />
      <Box sx={{ px: 1.5, pt: 1 }}>
        <Typography variant="caption" color="text.secondary">Profile</Typography>
      </Box>
      <List dense>
        <ListItemButton onClick={() => { navigate('/profile'); if (onClose) onClose(); }}>
          <ListItemIcon><PersonIcon /></ListItemIcon>
          <ListItemText primary="Profile" />
        </ListItemButton>
        <ListItemButton onClick={() => { if (onOpenDefaultPrompt) onOpenDefaultPrompt(); if (onClose) onClose(); }}>
          <ListItemIcon><EditNoteIcon /></ListItemIcon>
          <ListItemText primary="Default System Prompt" />
        </ListItemButton>
        <ListItemButton onClick={() => { navigate('/upgrade'); if (onClose) onClose(); }}>
          <ListItemIcon><UpgradeIcon /></ListItemIcon>
          <ListItemText primary="Upgrade" />
        </ListItemButton>
        <Divider sx={{ my: 1 }} />
        <ListItemButton onClick={() => { if (onLogout) onLogout(); if (onClose) onClose(); }}>
          <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error' }} />
        </ListItemButton>
      </List>
    </Drawer >
  );
}


